import {
  type CreatePackageFlightRequest,
  type PackageDetail,
  type PackageFlightOut,
} from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { ChevronDown, Plane, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useAddPackageFlight,
  useDeletePackageFlight,
  useUpdatePackageFlight,
} from '../use-packages';

import { PackageFlightFormDrawer } from './PackageFlightFormDrawer';

interface FlightsEditorProps {
  /** Live (edit) mode: the existing package whose flights are edited via the API. */
  pkg?: PackageDetail | null;
  /** Controlled (create) mode: staged flights held in the parent. */
  draftValue?: CreatePackageFlightRequest[];
  /** Controlled (create) mode: provide to switch to in-memory editing (no API). */
  onDraftChange?: (items: CreatePackageFlightRequest[]) => void;
}

/** Build a {@link PackageFlightOut}-shaped row from a draft flight for the list + edit drawer. */
const draftToRow = (f: CreatePackageFlightRequest, i: number): PackageFlightOut => ({
  id: `draft-${i}`,
  airlineId: f.airlineId ?? null,
  sectorFrom: f.sectorFrom ?? null,
  sectorTo: f.sectorTo ?? null,
  flightNumber: f.flightNumber ?? null,
  departureDate: f.departureDate ?? null,
  departureTime: f.departureTime ?? null,
  arrivalDate: f.arrivalDate ?? null,
  arrivalTime: f.arrivalTime ?? null,
  sortOrder: f.sortOrder ?? i,
});

export const FlightsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
}: FlightsEditorProps): React.JSX.Element => {
  const controlled = onDraftChange !== undefined;
  const draftItems = draftValue ?? [];

  // Hooks run unconditionally; in controlled mode the mutations never fire.
  const add = useAddPackageFlight(pkg?.id ?? '');
  const update = useUpdatePackageFlight(pkg?.id ?? '');
  const del = useDeletePackageFlight(pkg?.id ?? '');
  const [drawer, setDrawer] = useState<{
    mode: 'create' | 'edit';
    item?: PackageFlightOut;
    draftIndex?: number;
  } | null>(null);

  const rows: PackageFlightOut[] = controlled ? draftItems.map(draftToRow) : (pkg?.flights ?? []);
  const pending = controlled ? false : add.isPending || update.isPending || del.isPending;

  const submit = async (payload: CreatePackageFlightRequest): Promise<void> => {
    if (controlled) {
      const next =
        drawer?.mode === 'edit' && drawer.draftIndex !== undefined
          ? draftItems.map((d, i) => (i === drawer.draftIndex ? payload : d))
          : [...draftItems, payload];
      onDraftChange(next.map((f, i) => ({ ...f, sortOrder: i })));
      setDrawer(null);
      return;
    }
    if (drawer?.mode === 'edit' && drawer.item) {
      await update.mutateAsync({ childId: drawer.item.id, payload });
    } else {
      await add.mutateAsync(payload);
    }
    setDrawer(null);
  };

  const remove = (row: PackageFlightOut, i: number): void => {
    if (!window.confirm('Remove this flight?')) return;
    if (controlled) {
      onDraftChange(
        draftItems.filter((_, idx) => idx !== i).map((f, idx) => ({ ...f, sortOrder: idx })),
      );
      return;
    }
    del.mutate(row.id);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Flights</h3>
        <UzButton size="sm" onClick={() => setDrawer({ mode: 'create' })} disabled={pending}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add flight
        </UzButton>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No flights yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row, i) => {
            const isOpen = drawer?.mode === 'edit' && drawer.draftIndex === i;
            const toggle = (): void =>
              setDrawer(isOpen ? null : { mode: 'edit', item: row, draftIndex: i });
            return (
              <React.Fragment key={row.id}>
                <li
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md border border-zinc-200 px-3 py-2 hover:bg-zinc-50"
                >
                  <Plane className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                    {`${row.sectorFrom ?? '?'} → ${row.sectorTo ?? '?'}`}
                  </span>
                  {row.flightNumber && (
                    <span className="shrink-0 text-xs text-zinc-500">{row.flightNumber}</span>
                  )}
                  {row.departureDate && (
                    <span className="shrink-0 text-xs text-zinc-500 tabular-nums">
                      {row.departureDate}
                    </span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(row, i);
                    }}
                    disabled={pending}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    aria-label="Remove flight"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
                {isOpen && (
                  <li className="list-none">
                    <PackageFlightFormDrawer
                      mode="edit"
                      item={row}
                      pending={pending}
                      onClose={() => setDrawer(null)}
                      onSubmit={submit}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      )}

      {drawer?.mode === 'create' && (
        <PackageFlightFormDrawer
          mode="create"
          pending={pending}
          onClose={() => setDrawer(null)}
          onSubmit={submit}
        />
      )}
    </section>
  );
};
